import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Zap, Play, Pause, Volume2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

const presets = [
  { name: "Delta (Deep Sleep)", frequency: 2, color: "from-indigo-600 to-purple-600" },
  { name: "Theta (Meditation)", frequency: 6, color: "from-purple-600 to-pink-600" },
  { name: "Alpha (Relaxation)", frequency: 10, color: "from-cyan-600 to-blue-600" },
  { name: "Low Beta (Focus)", frequency: 14, color: "from-green-600 to-teal-600" },
  { name: "Beta (Alertness)", frequency: 20, color: "from-yellow-600 to-orange-600" },
  { name: "Gamma (Peak)", frequency: 40, color: "from-red-600 to-pink-600" },
];

const IsochronicTones = ({ audioContext, masterGain, isPlaying }) => {
  const [active, setActive] = useState(false);
  const [baseFrequency, setBaseFrequency] = useState(200);
  const [pulseRate, setPulseRate] = useState(10);
  const [volume, setVolume] = useState(50);
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  const oscillatorRef = useRef(null);
  const gainNodeRef = useRef(null);
  const pulseGainRef = useRef(null);
  const intervalRef = useRef(null);

  const startTones = useCallback(() => {
    if (!audioContext || !masterGain) return;
    
    // Clean up existing
    stopTones();
    
    // Create oscillator
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const pulseGain = audioContext.createGain();
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(baseFrequency, audioContext.currentTime);
    
    gainNode.gain.setValueAtTime(volume / 100, audioContext.currentTime);
    pulseGain.gain.setValueAtTime(1, audioContext.currentTime);
    
    oscillator.connect(gainNode);
    gainNode.connect(pulseGain);
    pulseGain.connect(masterGain);
    
    oscillator.start();
    
    oscillatorRef.current = oscillator;
    gainNodeRef.current = gainNode;
    pulseGainRef.current = pulseGain;
    
    // Create pulsing effect
    const pulsePeriod = 1000 / pulseRate;
    const pulseOn = pulsePeriod * 0.5; // 50% duty cycle
    
    let isOn = true;
    intervalRef.current = setInterval(() => {
      if (pulseGainRef.current && audioContext) {
        const now = audioContext.currentTime;
        if (isOn) {
          pulseGainRef.current.gain.setTargetAtTime(0, now, 0.01);
        } else {
          pulseGainRef.current.gain.setTargetAtTime(1, now, 0.01);
        }
        isOn = !isOn;
      }
    }, pulsePeriod / 2);
    
  }, [audioContext, masterGain, baseFrequency, pulseRate, volume]);

  const stopTones = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
        oscillatorRef.current.disconnect();
      } catch (e) {}
      oscillatorRef.current = null;
    }
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }
    if (pulseGainRef.current) {
      pulseGainRef.current.disconnect();
      pulseGainRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (active && isPlaying && audioContext) {
      startTones();
    } else {
      stopTones();
    }
    
    return () => stopTones();
  }, [active, isPlaying, audioContext, startTones, stopTones]);

  useEffect(() => {
    if (active && isPlaying && oscillatorRef.current) {
      oscillatorRef.current.frequency.setValueAtTime(baseFrequency, audioContext.currentTime);
    }
  }, [baseFrequency, active, isPlaying, audioContext]);

  useEffect(() => {
    if (gainNodeRef.current && audioContext) {
      gainNodeRef.current.gain.setValueAtTime(volume / 100, audioContext.currentTime);
    }
  }, [volume, audioContext]);

  useEffect(() => {
    if (active && isPlaying) {
      // Restart with new pulse rate
      startTones();
    }
  }, [pulseRate]);

  const handlePresetClick = (preset, index) => {
    setSelectedPreset(index);
    setPulseRate(preset.frequency);
  };

  const toggleActive = () => {
    setActive(!active);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Zap size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Isochronic Tones</h3>
            <p className="text-xs text-gray-500">Rhythmic pulses at specific frequencies</p>
          </div>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleActive}
          data-testid="isochronic-toggle"
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-all",
            active
              ? "bg-gradient-to-r from-amber-500 to-orange-500 shadow-[0_0_20px_rgba(251,191,36,0.4)]"
              : "bg-white/10 hover:bg-white/20"
          )}
        >
          {active ? <Pause size={20} className="text-white" /> : <Play size={20} className="text-white ml-0.5" />}
        </motion.button>
      </div>

      {/* Presets */}
      <div>
        <label className="text-xs text-gray-500 mb-2 block">Brainwave Presets</label>
        <div className="grid grid-cols-3 gap-2">
          {presets.map((preset, i) => (
            <motion.button
              key={preset.name}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handlePresetClick(preset, i)}
              data-testid={`isochronic-preset-${i}`}
              className={cn(
                "p-3 rounded-xl border transition-all text-left",
                selectedPreset === i
                  ? `bg-gradient-to-br ${preset.color} border-transparent`
                  : "bg-white/5 border-white/10 hover:border-white/20"
              )}
            >
              <span className={cn(
                "text-xs font-medium block",
                selectedPreset === i ? "text-white" : "text-gray-300"
              )}>
                {preset.name}
              </span>
              <span className={cn(
                "text-[10px]",
                selectedPreset === i ? "text-white/80" : "text-gray-500"
              )}>
                {preset.frequency} Hz
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Base Frequency */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-500">Base Tone Frequency</label>
          <span className="text-sm text-amber-400 font-mono">{baseFrequency} Hz</span>
        </div>
        <Slider
          value={[baseFrequency]}
          onValueChange={([v]) => setBaseFrequency(v)}
          min={100}
          max={500}
          step={10}
          data-testid="isochronic-base-freq"
          className="w-full"
        />
        <div className="flex justify-between text-[10px] text-gray-600 mt-1">
          <span>100 Hz</span>
          <span>500 Hz</span>
        </div>
      </div>

      {/* Pulse Rate */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-500">Pulse Rate (Entrainment)</label>
          <span className="text-sm text-amber-400 font-mono">{pulseRate} Hz</span>
        </div>
        <Slider
          value={[pulseRate]}
          onValueChange={([v]) => {
            setPulseRate(v);
            setSelectedPreset(null);
          }}
          min={1}
          max={50}
          step={0.5}
          data-testid="isochronic-pulse-rate"
          className="w-full"
        />
        <div className="flex justify-between text-[10px] text-gray-600 mt-1">
          <span>1 Hz (Delta)</span>
          <span>50 Hz (Gamma+)</span>
        </div>
      </div>

      {/* Volume */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <Volume2 size={12} />
            Volume
          </label>
          <span className="text-sm text-gray-400">{volume}%</span>
        </div>
        <Slider
          value={[volume]}
          onValueChange={([v]) => setVolume(v)}
          min={0}
          max={100}
          step={1}
          data-testid="isochronic-volume"
          className="w-full"
        />
      </div>

      {/* Visual Indicator */}
      {active && isPlaying && (
        <div className="flex items-center justify-center py-4">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 1, 0.5]
            }}
            transition={{
              duration: 1 / pulseRate,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 shadow-[0_0_40px_rgba(251,191,36,0.6)]"
          />
        </div>
      )}

      {/* Info */}
      <div className="text-xs text-gray-500 bg-white/5 rounded-lg p-3">
        <p className="mb-1"><strong className="text-gray-400">How it works:</strong></p>
        <p>Isochronic tones use rhythmic pulses of a single tone to entrain brainwaves. Unlike binaural beats, they don't require headphones and can be more effective for some people.</p>
      </div>
    </div>
  );
};

export default IsochronicTones;
